import { forwardRef, type PropsWithChildren } from "react";
import { useScrollLock } from "usehooks-ts";

import CloseIcon from "$/lib/icons/CloseIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

import type { ModalProps } from "./Modal.types";

const Modal = forwardRef<HTMLDivElement, PropsWithChildren<ModalProps>>(
  (
    {
      isOpen,
      onClose,
      keepOpenOnOutsideClick,
      className,
      hideCloseButton,
      children,
      backdropClassName,
      headerSlot,
      contentBoxClassName,
    },
    forwardedRef,
  ) => {
    useScrollLock();

    // Boîte principale inchangée quel que soit headerSlot — même JSX, même
    // ref, mêmes classes — pour garantir qu'aucun usage existant du Modal
    // n'est affecté quand headerSlot n'est pas fourni.
    const mainBox = (
      <div
        ref={forwardedRef}
        className={cn(
          "max-h-full w-fit max-w-[calc(100vw-2rem)] transform overflow-y-auto overflow-x-visible rounded-2xl bg-white p-6 text-black shadow-lg screen850:max-w-5xl",
          { "animate-popup": isOpen },
          className,
          contentBoxClassName,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {!hideCloseButton && (
          <div className="absolute right-3 top-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1.5 hover:bg-info"
            >
              <CloseIcon className="size-3.5" />
            </button>
          </div>
        )}
        <div>{children}</div>
      </div>
    );

    return (
      <>
        {isOpen && (
          <div
            className={cn(
              // h-dvh (dynamic viewport height) plutôt que h-screen (100vh
              // figé) : sur mobile, 100vh est calculé sur la plus grande
              // hauteur possible (barre d'adresse/clavier fermés) et ne se
              // met pas à jour — un élément fixed dimensionné dessus déborde
              // donc sous la barre d'adresse ou le clavier virtuel une fois
              // ouvert(e), coupant le bas du contenu sans qu'aucun scroll ne
              // puisse le révéler (la zone scrollable elle-même est mal
              // dimensionnée, pas juste son contenu). dvh suit la hauteur
              // réellement visible. Sans effet sur desktop (dvh = vh, pas de
              // barre d'outils dynamique).
              "fixed inset-0 z-[60] flex h-dvh w-full items-center justify-center overflow-hidden bg-primary/10 backdrop-blur-[2px]",
              backdropClassName,
            )}
            onClick={!keepOpenOnOutsideClick ? onClose : undefined}
          >
            {headerSlot ? (
              <div
                className={cn(
                  // overflow-y-auto ici (pas seulement sur mainBox) : le
                  // "max-h-full" de mainBox ne peut se résoudre correctement
                  // que si CE conteneur a une hauteur définie — dans une
                  // chaîne flex-col où seul le grand-parent (h-screen) a une
                  // hauteur réelle, "max-height: 100%" sur mainBox reste non
                  // résolu et son propre overflow-y-auto ne s'active jamais,
                  // ce qui coupait le bas des steps trop hauts (mobile) sans
                  // possibilité de scroller. Le scroll porte donc sur
                  // l'ensemble header+contenu plutôt que sur mainBox seul.
                  "flex max-h-full max-w-[calc(100vw-2rem)] flex-col items-center gap-3 overflow-y-auto screen850:max-w-5xl",
                  // Même largeur que la boîte principale (className) : évite
                  // toute ambiguïté de résolution CSS entre un parent
                  // "w-fit" et un enfant "w-full" — les deux boîtes doivent
                  // rester alignées visuellement.
                  className,
                )}
              >
                <div
                  // shadow-xs (au lieu de shadow-lg) : headerSlot n'est
                  // utilisé que par ProfileWizardModal, aucun autre usage du
                  // Modal ne passe cette prop — ombre ajustée uniquement sur
                  // cette boîte, à la demande.
                  // Largeur dynamique (w-fit) : le header se dimensionne sur
                  // son propre contenu (nombre d'étapes), plutôt que sur une
                  // largeur fixe alignée sur la boîte principale en dessous.
                  className="w-fit max-w-full rounded-2xl bg-white px-5 screen850:px-7 py-3 shadow-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  {headerSlot}
                </div>
                {mainBox}
              </div>
            ) : (
              mainBox
            )}
          </div>
        )}
      </>
    );
  },
);

Modal.displayName = "Modal";

export default Modal;
